import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Zap, AlertCircle } from 'lucide-react';

type SubscriptionPlan = 'free' | 'solo' | 'team' | 'agency';

interface BoostQuotaIndicatorProps {
  remainingQuota: number;
  totalQuota: number;
  plan: SubscriptionPlan;
  isInCooldown: boolean;
  cooldownRemainingMs: number;
  getQuotaMessage: () => string;
  getCooldownMessage: () => string;
  className?: string;
}

/**
 * Component to display boost quota and cooldown status
 * Shows remaining boosts, cooldown timer, and upgrade prompts
 */
export const BoostQuotaIndicator: React.FC<BoostQuotaIndicatorProps> = ({
  remainingQuota,
  totalQuota,
  plan,
  isInCooldown,
  cooldownRemainingMs,
  getQuotaMessage,
  getCooldownMessage,
  className = ""
}) => {
  const { t } = useTranslation();
  
  // Calculate progress percentage
  const usedQuota = totalQuota - remainingQuota;
  const progressPercentage = (usedQuota / totalQuota) * 100;
  
  // Determine status color
  const getStatusColor = () => {
    if (remainingQuota === 0) return 'text-red-600';
    if (remainingQuota <= totalQuota * 0.2) return 'text-orange-600';
    return 'text-green-600';
  };
  
  // Determine border color for the card
  const getBorderColor = () => {
    if (remainingQuota === 0) return 'border-red-500';
    return 'border-green-500';
  };
  
  // Determine progress bar color
  const getProgressColor = () => {
    if (remainingQuota === 0) return 'bg-red-500';
    if (remainingQuota <= totalQuota * 0.2) return 'bg-orange-500';
    return 'bg-green-500';
  };
  
  // Get upgrade suggestion
  const getUpgradeMessage = () => {
    if (remainingQuota > 0 || plan !== 'free') return null;
    
    return t('boostMode.rateLimiting.upgradeFromFree');
  };
  
  const upgradeMessage = getUpgradeMessage();
  
  return (
    <div className={`bg-white rounded-lg border ${getBorderColor()} p-4 ${className}`} style={{ borderWidth: '1px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Zap className={`h-5 w-5 ${getStatusColor()}`} />
          <span className="font-medium text-gray-900">{t('boostMode.quotaCard.title')}</span>
        </div>
        <span className={`text-sm font-semibold ${getStatusColor()}`}>
          {getQuotaMessage()}
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>{t('boostMode.quotaCard.used')}: {usedQuota}</span>
          <span>{t('boostMode.quotaCard.total')}: {totalQuota}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>
      
      {/* Cooldown Status */}
      {isInCooldown && (
        <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded-md mb-3">
          <Clock className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            {getCooldownMessage()}
          </span>
        </div>
      )}
      
      {/* Limit Reached Message */}
      {remainingQuota === 0 && !isInCooldown && (
        <div className="p-3 bg-red-50 rounded-md">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800">
                {t('boostMode.rateLimiting.dailyLimitReached')}
              </h4>
              <p className="text-sm text-red-700 mt-1">
                {t('boostMode.rateLimiting.dailyLimitReachedMessage')}
              </p>
              {upgradeMessage && (
                <p className="text-sm font-medium text-red-800 mt-2">
                  💡 {upgradeMessage}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Warning when approaching limit */}
      {remainingQuota > 0 && remainingQuota <= Math.max(1, totalQuota * 0.2) && (
        <div className="p-2 bg-orange-50 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <span className="text-sm text-orange-800">
              {remainingQuota === 1 
                ? t('boostMode.quotaCard.lastBoostToday')
                : t('boostMode.quotaCard.remainingBoosts', { count: remainingQuota })
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};